/*!
 * Created by netlogix GmbH & Co. KG
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { animate, style, transition, trigger } from '@angular/animations';
import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { State } from '@core/store';
import { DriversService } from '@drivers/services/drivers.service';
import { Store } from '@ngrx/store';
import { Driver, Vehicle } from '@shared/models';
import { ToursService } from '@tours/services/tours.service';
import { getCurrentTour } from '@tours/store/tours.selectors';
import { TeamForm } from '@tours/tour-detail/form-drivers/_models/team-form.model';
import { VehiclesService } from '@vehicles/services/vehicles.service';
import { Observable, Subscription } from 'rxjs';
import { filter, map, switchMap, withLatestFrom } from 'rxjs/operators';

@Component({
    selector: 'app-form-drivers',
    templateUrl: './form-drivers.component.html',
    styleUrls: ['./form-drivers.component.scss'],
    animations: [
        trigger('showWarning', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('100ms', style({ opacity: 1 }))
            ]),
            transition(':leave', [
                style({ opacity: 1 }),
                animate('100ms', style({ opacity: 0 }))
            ])
        ])
    ],
    standalone: false
})
export class FormDriversComponent implements OnInit, OnDestroy {
  @Input() parentForm: UntypedFormGroup;
  @Input() tourDate: Observable<Date>;

  vehicles: Vehicle[];
  drivers: Driver[];
  vehicleConflicts = [];

  private vehiclesSubscription: Subscription;
  private driversSubscription: Subscription;
  private vehicleConflictsSubscription: Subscription;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private vehiclesService: VehiclesService,
    private driversService: DriversService,
    private datePipe: DatePipe,
    private toursService: ToursService,
    private store: Store<State>,
    private changeListener: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.vehiclesSubscription = this.tourDate.pipe(
      switchMap((date: Date) => this.vehiclesService.getVehicles({
        enabled: '1',
        ...(date && { noAbsenceOn: this.datePipe.transform(date, 'yyyy-MM-dd') })
      }))
    ).subscribe(vehicles => {
      this.vehicles = vehicles;
      this.changeListener.markForCheck();
    });

    this.driversSubscription = this.tourDate.pipe(
      switchMap((date: Date) => this.driversService.getDrivers({
        noAbsenceOn: date ? this.datePipe.transform(date, 'yyyy-MM-dd') : null,
        enabled: 1
      }))
    ).subscribe(drivers => {
      this.drivers = drivers;
      this.changeListener.markForCheck();
    });

    this.vehicleConflictsSubscription = this.tourDate.pipe(
      filter(date => date !== null),
      switchMap((date: Date) => this.toursService.getToursForDay(date)),
      map(tours => tours.filter(tour => tour['@id'] !== '')),
      withLatestFrom(this.store.select(getCurrentTour).pipe(
        filter(tour => !!tour)
      )),
      map(([tours, currentTour]) => {
          const conflicts = [];
          for (const tour of tours) {
            if (tour['@id'] === currentTour['@id']) {
              continue;
            }
            for (const team of tour.teams) {
              if (null === team.vehicle) {
                continue;
              }

              if (undefined === conflicts[team.vehicle['@id']]) {
                conflicts[team.vehicle['@id']] = [];
              }
              conflicts[team.vehicle['@id']].push(tour['@id']);
            }
          }
          return conflicts;
        }
      )
    ).subscribe(
      (conflicts) => this.vehicleConflicts = conflicts
    );
  }

  onAddDriver(team: UntypedFormGroup): void {
    const control = new UntypedFormControl(null, Validators.required);
    (team.get('additionalDrivers') as UntypedFormArray).push(control);
  }

  onRemoveDriver(team: UntypedFormGroup, driverIndex: number): void {
    (team.get('additionalDrivers') as UntypedFormArray).removeAt(driverIndex);
  }

  onAddTeam(): void {
    this.teamsForm.push(this.formBuilder.group(new TeamForm()));
  }

  onRemoveTeam(teamIndex: number): void {
    this.teamsForm.removeAt(teamIndex);
  }

  get teamsForm(): UntypedFormArray {
    return (this.parentForm.get('teams') as UntypedFormArray);
  }

  ngOnDestroy(): void {
    if (this.vehicleConflictsSubscription) {
      this.vehicleConflictsSubscription.unsubscribe();
    }
    if (this.vehiclesSubscription) {
      this.vehiclesSubscription.unsubscribe();
    }
    if (this.driversSubscription) {
      this.driversSubscription.unsubscribe();
    }
  }

  onVehicleChange(event: MatSelectChange, team: UntypedFormGroup): void {
    const selectedVehicle = this.vehicles.find(existingVehicle => existingVehicle['@id'] === event.value);
    const possibleDrivers = this.drivers.filter(driver => selectedVehicle.drivers.indexOf(driver['@id']) !== -1)
      .map(driver => driver['@id']);

    for (const driver of possibleDrivers) {
      if (!this.assignedDriverIds.includes(driver)) {
        team.patchValue({
          mainDriver: driver
        });
        break;
      }
    }
  }

  get assignedVehicleIds(): string[] {
    return this.teamsForm.value.map(team => team.vehicle)
      .filter(vehicle => vehicle);
  }

  get assignedDriverIds(): string[] {
    return this.teamsForm.value.flatMap(team => [team.mainDriver].concat(team.additionalDrivers));
  }
}
